import type { PasswordPolicy } from '../api/types';
export type { PasswordPolicy } from '../api/types';

/**
 * Render the policy as a single-line helper string. Mirrors the rules
 * the server enforces — e.g. "At least 12 characters; must include
 * an uppercase letter, a lowercase letter, a digit, and a special
 * character." Only required rules are listed.
 */
export function describePasswordPolicy(p: PasswordPolicy): string {
  const reqs: string[] = [];
  if (p.requireUppercase)   reqs.push('an uppercase letter');
  if (p.requireLowercase)   reqs.push('a lowercase letter');
  if (p.requireDigit)       reqs.push('a digit');
  if (p.requireSpecialChar) reqs.push('a special character');

  const lengthClause = `At least ${p.minLength} characters`;
  if (reqs.length === 0) return `${lengthClause}.`;

  const list =
    reqs.length === 1 ? reqs[0]
    : reqs.length === 2 ? `${reqs[0]} and ${reqs[1]}`
    : `${reqs.slice(0, -1).join(', ')}, and ${reqs[reqs.length - 1]}`;

  return `${lengthClause}; must include ${list}.`;
}

/** Returns true when the password satisfies every required rule. */
export function validatePassword(pw: string, p: PasswordPolicy): boolean {
  if (pw.length < p.minLength) return false;
  if (p.requireUppercase   && !/[A-Z]/.test(pw)) return false;
  if (p.requireLowercase   && !/[a-z]/.test(pw)) return false;
  if (p.requireDigit       && !/[0-9]/.test(pw)) return false;
  if (p.requireSpecialChar && /^[A-Za-z0-9]+$/.test(pw)) return false;
  return true;
}
